'use client'

import { useState, type ReactNode } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import Link from '@/components/ui/Link'
import { AppInfo } from '@/components/content/app/AppInfo'
import AppIcon from '@/components/content/app/AppIcon'
import Badge from '@/components/ui/Badge'
import VideoModal from '@/components/ui/VideoModal'
import { Icon } from '@/icons/Icon'
import { Recommendation } from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { getBrandColors, cn, scrollToSection, isPopulatedObject } from '@/lib/utils'
import { checkAppBranding } from '@/lib/formatting'
import { type FeaturedPlacement, trackFeaturedClick } from '@/lib/analytics'
import { getAppRecommendation } from './helpers'
import RankIndicator, { type RankChange } from '@/components/ui/RankIndicator'
import type { RankChangesByAcrId } from '@/app/(app)/best/[category]/fetchers'
import type {
  AppCategoryRecommendation,
  App,
  Category,
  Deal,
  ReviewCategoryCriterionRating,
  Video,
} from '@/payload-types'

export type RankProps = 'ordered' | 'grouped' | 'blank' | 'none'

type AppRankingServerData = Map<
  number,
  {
    ratings: Pick<ReviewCategoryCriterionRating, 'rating' | 'isApplicable' | 'categoryCriterion'>[]
    deals: Deal[]
  }
>

type AppRankingProps = ThemeProps & {
  title?: ReactNode
  apps: AppCategoryRecommendation[]
  otherApps?: AppCategoryRecommendation[]
  focus?: string | null
  category: Category
  compareWithApp?: App
  rank?: RankProps
  isBest?: boolean
  className?: string
  collapse?: boolean
  video?: Pick<
    Video,
    'id' | 'thumbnail' | 'name' | 'url' | 'duration' | 'youtubeId' | 'thumbnailOverride'
  >
  partnership?: boolean
  flag?: string[]
  serverDataByAppId?: AppRankingServerData
  rankChangesByAcrId?: RankChangesByAcrId
  featuredPlacement?: FeaturedPlacement
  showCategoryBadge?: boolean
  // When set, overrides the rank number shown per app with its true position on
  // that category's /best page (looked up by app id), instead of this app's
  // position within just the `apps` passed in here. An app with no entry (not on
  // the category's main ranked list) shows a dash rather than a made-up number.
  rankByAppId?: Partial<Record<App['id'], number>>
}

type AppRankProps = ThemeProps & {
  app: App
  index: number | null
  rank?: RankProps
  rankChange?: RankChange
  description?: string
  compareWithApp?: Pick<App, 'id' | 'slug' | 'name'>
  recommendation?: Recommendation
  bestFor?: string
  fullWidth?: boolean
  className?: string
  focus?: string | null
  category?: Category
  flag?: string[]
  serverRatings?: Pick<
    ReviewCategoryCriterionRating,
    'rating' | 'isApplicable' | 'categoryCriterion'
  >[]
  serverDeals?: Deal[]
  featuredPlacement?: FeaturedPlacement
  showCategoryBadge?: boolean
}

type AppRankingTitleProps = ThemeProps & {
  title: string
  icon?: string | null
  link?: string
  parentLink?: string
  text?: string
  apps?: AppCategoryRecommendation[]
  tag?: 'h2' | 'h3' | 'h4' | 'span'
}

type BonusAppsProps = ThemeProps & {
  apps: AppCategoryRecommendation[]
  categorySlug: string
  isBest?: boolean
}

type ThemeProps = {
  theme?: 'light' | 'dark'
}

export default function AppRanking({
  title = false,
  apps,
  otherApps,
  focus,
  category,
  compareWithApp,
  theme = 'light',
  rank = 'ordered',
  isBest,
  className,
  collapse,
  video,
  flag,
  serverDataByAppId,
  rankChangesByAcrId,
  featuredPlacement,
  showCategoryBadge = false,
  rankByAppId,
}: AppRankingProps) {
  const pathname = usePathname()
  const currentPath = pathname.split('/')
  const isCompare = currentPath[1] === 'compare'

  const hasOtherApps = otherApps && otherApps.length > 0
  const passedFlag = apps.length === 1 ? [...(flag ?? []), 'trending'] : flag

  const [show, setShow] = useState(!collapse)

  const categoryId = category?.id

  // Guard against undefined category after hooks
  if (!categoryId) return null

  return (
    <div className={cn('mx-auto flex w-full flex-col gap-8', className)}>
      <ol
        className={cn(
          'flex flex-col rounded-xl border',
          focus ? '' : 'divide-y',
          theme === 'dark'
            ? rank === 'ordered' || rank === 'grouped' || collapse
              ? 'divide-white/15 border-white/15 bg-black'
              : 'divide-zinc-900 border-zinc-900 bg-black'
            : 'divide-gray-200 border-gray-200 bg-white dark:divide-white/15 dark:border-white/15 dark:bg-black',
        )}
      >
        {title &&
          (collapse ? (
            <button
              type="button"
              onClick={() => setShow((prev) => !prev)}
              aria-expanded={show}
              className="group flex w-full items-center justify-between"
            >
              {title}
              <div
                className={cn(
                  'relative z-30 mr-2.5 flex h-8 w-8 -rotate-90 cursor-pointer items-center justify-center transition-all',
                  'text-zinc-600 group-hover:text-gray-600 dark:group-hover:text-white',
                  show && 'rotate-90',
                )}
              >
                <Icon name={'chevron-left'} size={12} />
              </div>
            </button>
          ) : (
            <div className="group">{title}</div>
          ))}

        {apps.map((acr, index) => {
          const app = acr.app
          // Skip if app is not populated (is a number ID) or missing
          if (!app || typeof app === 'number') return null
          const rowCategory = isPopulatedObject<Category>(acr.category) ? acr.category : category
          const firstApp = apps[0]?.app
          const compareWithFirstApp = compareWithApp
            ? compareWithApp
            : index > 0 && !isCompare && isPopulatedObject<App>(firstApp) && firstApp.slug
              ? { id: firstApp.id, slug: firstApp.slug, name: firstApp.name }
              : undefined
          const { recommendation, bestFor } = getAppRecommendation({
            app,
            categoryId: categoryId,
            appCategoryRecommendation: acr,
          })
          const displayedRank = rankByAppId ? (rankByAppId[app.id] ?? null) : index + 1
          const rankChange = rankChangesByAcrId?.[String(acr.id)]
          const visibleRankChange =
            acr.displayRankDelta !== false &&
            rankChange &&
            displayedRank !== null &&
            rankChange.previous !== displayedRank
              ? rankChange
              : undefined

          return (
            <AppRank
              app={app}
              key={app.id}
              index={displayedRank}
              theme={theme}
              compareWithApp={compareWithFirstApp}
              recommendation={recommendation}
              bestFor={bestFor ?? undefined}
              rank={rank}
              rankChange={visibleRankChange}
              focus={focus}
              category={rowCategory}
              flag={passedFlag}
              serverRatings={serverDataByAppId?.get(app.id)?.ratings}
              serverDeals={serverDataByAppId?.get(app.id)?.deals}
              featuredPlacement={featuredPlacement}
              showCategoryBadge={showCategoryBadge}
              className={collapse && Boolean(title) && !show ? 'hidden' : ''}
            />
          )
        })}
      </ol>

      {(hasOtherApps || video) && (
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          {video && (
            <VideoModal
              label="Watch the Full Breakdown"
              video={video}
              theme="dark"
              thumbnailQuality="medium"
            />
          )}
          {hasOtherApps && (
            <BonusApps
              apps={otherApps}
              theme={theme}
              categorySlug={category.slug}
              isBest={isBest}
            />
          )}
        </div>
      )}
    </div>
  )
}

export function AppRank({
  app,
  index,
  theme,
  rank,
  rankChange,
  description,
  compareWithApp,
  recommendation,
  bestFor,
  fullWidth,
  className,
  focus,
  category,
  flag,
  serverRatings,
  serverDeals,
  featuredPlacement,
  showCategoryBadge,
}: AppRankProps) {
  const router = useRouter()
  const resolvedHref: string | undefined = app.slug ? `/apps/${app.slug}` : undefined

  const handleActivate = () => {
    if (!resolvedHref) return
    if (featuredPlacement) trackFeaturedClick(featuredPlacement, app.name)

    if (app.slug) {
      const target = document.getElementById(app.slug)
      if (target) {
        scrollToSection({
          id: app.slug,
          offset: { mobile: 200, desktop: 80 },
          animate: true,
          hideHash: true,
        })
        return
      }
    }

    router.push(resolvedHref)
  }

  return (
    <li
      className={cn(
        focus && focus !== app.name
          ? 'hidden'
          : cn(
              'relative flex min-h-14 gap-2.5 sm:gap-5',
              focus ? 'rounded-xl' : 'first:rounded-t-xl last:rounded-b-xl',
            ),
        !fullWidth && 'py-3 pl-3 pr-3 sm:py-3.5 sm:pl-4 sm:pr-0',
        theme === 'dark'
          ? rank === 'ordered' || rank === 'grouped'
            ? 'bg-white/5'
            : 'bg-black'
          : rank === 'ordered' || rank === 'grouped'
            ? 'bg-gray-50 dark:bg-white/5'
            : 'bg-white dark:bg-black',
        className,
      )}
    >
      {rank === 'ordered' && (
        <RankIndicator
          rank={index}
          rankChange={rankChange}
          theme={theme}
          onClickAction={handleActivate}
        />
      )}
      <AppInfo
        app={app}
        recommendation={recommendation ?? Recommendation.NotRecommended}
        bestFor={bestFor}
        theme={theme}
        description={description}
        compareWithApp={compareWithApp}
        tag="span"
        category={category}
        rank={rank}
        serverRatings={serverRatings}
        serverDeals={serverDeals}
        onClickAction={handleActivate}
        flag={flag}
        featuredPlacement={featuredPlacement}
        showCategoryBadge={showCategoryBadge}
      />
    </li>
  )
}

export function AppRankingTitle({
  title,
  icon,
  text,
  link,
  parentLink,
  apps,
  theme,
  tag: Tag = 'span',
}: AppRankingTitleProps) {
  const pathname = usePathname()
  const effectiveParentLink = parentLink && parentLink !== pathname ? parentLink : undefined

  const containerClass =
    'relative group flex h-12 w-full items-center justify-between overflow-hidden rounded-t-[11px] px-4'
  const content = (
    <>
      <div
        className={cn(
          'flex min-w-0 flex-1 items-center gap-3.5',
          apps?.length ? 'gap-0' : undefined,
        )}
      >
        {icon && (
          <div
            className={cn(
              'hidden h-7 w-7 shrink-0 items-center justify-center rounded-md border backdrop-blur-sm sm:flex',
              theme === 'dark'
                ? 'border-white/15 bg-black text-white'
                : 'border-gray-200 bg-white text-gray-500 dark:border-white/15 dark:bg-black dark:text-white',
            )}
          >
            <Icon name={icon} size={14} />
          </div>
        )}

        {apps && apps.length > 0 && (
          <div className="flex items-center">
            {apps.map((acr) => {
              const app = acr.app
              if (!app || typeof app === 'number') return null

              const { appIcon, appGlyph } = checkAppBranding(app)
              const { backgroundColor } = getBrandColors(app)

              return (
                <AppIcon
                  key={app.id}
                  icon={appIcon}
                  glyph={appGlyph}
                  name={app.name}
                  background={backgroundColor}
                  size={24}
                  invert={true}
                  className="-mr-2 -rotate-[8deg] transition-all last:mr-3.5 group-hover:mr-2 group-hover:rotate-0 last:group-hover:mr-3.5"
                />
              )
            })}
          </div>
        )}

        <Tag
          className={cn(
            'truncate text-14 font-semibold',
            theme === 'dark' ? 'text-white' : 'text-gray-950 dark:text-white',
          )}
        >
          {title}
        </Tag>
      </div>

      <div className="relative z-10 flex shrink-0 items-center gap-3.5">
        {text && (
          <span
            className={cn(
              'ml-2.5 text-13',
              theme === 'dark' ? 'text-white/60' : 'text-gray-500 dark:text-white/60',
            )}
          >
            {text}
          </span>
        )}
        {link && (
          <div
            className={cn(
              'flex h-5 rotate-180 items-center justify-center',
              theme === 'dark' ? 'text-white' : 'text-gray-500 dark:text-white',
            )}
          >
            <Icon name="chevron-left" size={12} />
          </div>
        )}
        {effectiveParentLink && (
          <div className="text-13 font-medium text-zinc-400 underline underline-offset-2 transition-colors group-hover:text-primary-500">
            See all
          </div>
        )}
      </div>

      {text && (
        <div className="absolute inset-0 z-0 translate-x-20 bg-gradient-to-l from-pink-500/25 from-10% to-transparent to-40% transition-transform duration-1000 group-hover:translate-x-0" />
      )}
    </>
  )

  if (link || effectiveParentLink) {
    return (
      <Link href={link ?? effectiveParentLink!} className={containerClass}>
        {content}
      </Link>
    )
  } else {
    return <div className={containerClass}>{content}</div>
  }
}

export function BonusApps({ apps, theme, categorySlug, isBest }: BonusAppsProps) {
  const pathname = usePathname()
  const router = useRouter()

  if (!apps.length) {
    return null
  }

  const handleClick = () => {
    if (pathname === '/') {
      router.push(`/best/${categorySlug}`)
    } else {
      scrollToSection({
        id: isBest ? 'apps-worth-trying' : 'notable-mentions',
        offset: isBest ? 80 : 150,
        animate: true,
        hideHash: true,
      })
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={`${apps.length} ${isBest ? 'apps worth trying' : 'notable mentions'}`}
      data-testid="bonus-apps"
      className={cn(
        'flex max-w-content items-center gap-2.5 rounded-xl border py-2.5 pl-2.5 pr-3.5 transition-colors',
        theme === 'dark'
          ? 'border-white/15 bg-white/5 hover:border-white/25'
          : 'border-gray-200 bg-gray-50 hover:border-gray-300 dark:border-white/15 dark:bg-white/5 dark:hover:border-white/25',
      )}
    >
      <Badge style="success" rounded={false} className="hidden sm:flex md:h-6">
        Bonus
      </Badge>

      <div className="flex items-center gap-3">
        <p
          className={cn(
            'text-14',
            theme === 'dark'
              ? 'text-gray-400 [&>span]:text-white'
              : 'text-gray-500 dark:text-gray-400 [&>span]:text-black dark:[&>span]:text-white',
          )}
        >
          <span>{apps.length}</span>{' '}
          {isBest
            ? `app${apps.length === 1 ? '' : 's'} worth trying`
            : `notable mention${apps.length === 1 ? '' : 's'}`}
        </p>

        <div
          className={cn(
            'rotate-180',
            theme === 'dark' ? 'text-white' : 'text-black dark:text-white',
          )}
        >
          <Icon name="chevron-left" size={12} />
        </div>
      </div>
    </button>
  )
}
